import { AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@sportscanner/ui";

export type ImpactSeverity = "block" | "warn";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  severity: ImpactSeverity;
  count: number;
  title: string;
  // Explains the impact in plain language. Caller composes per change kind
  // (duration change, sport switch, hide, hours narrowing, …).
  description: string;
  // Block variant only shows a Close button. Warn variant adds Confirm.
  confirmLabel?: string;
  cancelLabel?: string;
  isPending?: boolean;
  onConfirm?: () => void;
}

export default function BookingImpactDialog({
  open,
  onOpenChange,
  severity,
  count,
  title,
  description,
  confirmLabel,
  cancelLabel,
  isPending = false,
  onConfirm,
}: Props) {
  const { t } = useTranslation();

  const Icon = severity === "block" ? ShieldAlert : AlertTriangle;
  const iconClass = severity === "block" ? "text-red-500" : "text-amber-500";
  const actionClass =
    severity === "block"
      ? "bg-red-600 hover:bg-red-700"
      : "bg-amber-600 hover:bg-amber-700";

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        if (isPending) return;
        onOpenChange(o);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${iconClass}`} />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span className="block">{description}</span>
            <span className="block text-neutral-700 font-medium">
              {t("bookingImpact.affectedCount", { count })}
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {cancelLabel ??
              (severity === "block" ? t("common.close") : t("common.cancel"))}
          </AlertDialogCancel>
          {severity === "warn" && onConfirm ? (
            <AlertDialogAction
              disabled={isPending}
              onClick={(e) => {
                e.preventDefault();
                onConfirm();
              }}
              className={actionClass}
            >
              {isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                (confirmLabel ?? t("common.confirm"))
              )}
            </AlertDialogAction>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
